import { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

export const PublishModal = ({ project, open, onClose, onPublished }) => {
  const [title, setTitle] = useState(project?.title || project?.topic || "");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState(project?.topic || "");
  const [privacy, setPrivacy] = useState("private");
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [thumbReady, setThumbReady] = useState(false);
  const [thumbNonce, setThumbNonce] = useState(0);

  useEffect(() => {
    if (!open || !project) return;
    if (project.seo_title) {
      setTitle(project.seo_title);
      setDescription(project.seo_description || "");
      setTags((project.seo_tags || []).join(", "));
      setThumbReady(!!project.seo_thumbnail_filename);
      setThumbNonce((n) => n + 1);
      return;
    }
    setThumbReady(false);
    const generateMeta = async () => {
      setGenerating(true);
      try {
        const res = await api.post(`/projects/${project.id}/seo-meta`);
        setTitle(res.data.title || title);
        setDescription(res.data.description || "");
        setTags((res.data.tags || []).join(", "));
        setThumbReady(!!res.data.thumbnail_ready);
        setThumbNonce((n) => n + 1);
      } catch (e) {
        toast.error("Could not auto-generate SEO title/description - you can still edit manually");
      } finally {
        setGenerating(false);
      }
    };
    generateMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/projects/${project.id}/publish`, {
        title,
        description,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        privacy_status: privacy,
      });
      toast.success("Published to YouTube!");
      onPublished(res.data.youtube_video_id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="publish-youtube-modal" className="bg-slate-900 border-slate-800 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Publish to YouTube
            {generating && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Review the AI-generated title, description, tags and thumbnail before publishing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {generating && (
            <p data-testid="seo-generating-indicator" className="text-xs font-mono text-amber-400 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AI is generating an SEO title, description, tags & thumbnail...
            </p>
          )}

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">AI Thumbnail</label>
            {project?.orientation === "shorts" ? (
              <p data-testid="shorts-no-thumbnail-notice" className="text-xs text-slate-500 bg-slate-950/60 border border-slate-800 rounded-xl p-3">
                Shorts don&apos;t use a custom thumbnail on YouTube, so none is generated for this video.
              </p>
            ) : (
              <div className="w-full aspect-video bg-slate-950 rounded-xl border border-slate-800 flex items-center justify-center overflow-hidden">
                {thumbReady ? (
                  <img
                    data-testid="seo-thumbnail-preview"
                    src={`${API}/projects/${project?.id}/seo-thumbnail?v=${thumbNonce}`}
                    alt="AI generated thumbnail"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs text-slate-600">{generating ? "Generating thumbnail..." : "No thumbnail yet"}</span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Title</label>
            <input
              data-testid="publish-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-slate-950/80 border border-slate-800 focus:border-amber-500 text-slate-100 rounded-xl p-3 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Description</label>
            <textarea
              data-testid="publish-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-slate-950/80 border border-slate-800 focus:border-amber-500 text-slate-100 rounded-xl p-3 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Tags (comma separated)</label>
            <input
              data-testid="publish-tags-input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full bg-slate-950/80 border border-slate-800 focus:border-amber-500 text-slate-100 rounded-xl p-3 outline-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Privacy</label>
            <select
              data-testid="publish-privacy-select"
              value={privacy}
              onChange={(e) => setPrivacy(e.target.value)}
              className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-500 outline-none"
            >
              <option value="private">Private</option>
              <option value="unlisted">Unlisted</option>
              <option value="public">Public</option>
            </select>
          </div>
          <button
            data-testid="publish-confirm-button"
            onClick={submit}
            disabled={submitting || !title.trim()}
            className="w-full bg-red-600 hover:bg-red-500 text-white font-medium px-4 py-3 rounded-xl transition-all disabled:opacity-60"
          >
            {submitting ? "Publishing..." : "Publish Now"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
